<template>
  <div>
    <RouteHeader title="Detalle de Ruta" />
    <hr>
    <div v-if="isAllLoading" class="text-center">
      <b-spinner variant="primary" label="Loading..."></b-spinner>
    </div>
    <div v-else>
      <RouteName :route-date="routeData.date" :route-name="routeData.name" :route-status="routeData.status"/>
      <hr>
      <StatusForm :status="routeData.status" @onUpdateStatus="onUpdateStatus"/>

      <template v-for="(item, idx) in routeData.orderedClients">
        <ClientCard
          :route-data="routeData.clients[item[0]]"
          :client="getClient(item[0])"
          :products="products"
          :key="idx"
          @saveQuantity="saveQuantity"
          @saveDeliveryNote="saveDeliveryNote"
          @saveDeliveryComment="saveDeliveryComment"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { dateMixin } from '@/mixins/dateMixin'
import RouteHeader from '../RouteHeader'
import StatusForm from './RouteFormStatus'
import RouteName from './RouteName'

import ClientCard from './ClientCard'

export default {
  name: 'RouteDetail',
  components: {
    ClientCard,
    RouteName,
    StatusForm,
    RouteHeader
  },
  mixins: [dateMixin],
  props: {
    routeId: {
      // Passed via router
      type: String,
      required: true
    }
  },
  data () {
    return {
      routeData: {},
      products: {},
      clients: [],
      hasFinished: {
        route: false,
        products: false,
        clients: false
      }
    }
  },
  computed: {
    isAllLoading () {
      const val = this.hasFinished.route && this.hasFinished.clients && this.hasFinished.products
      return !val
    },
    carInfo () {
      return {
        id: 'furgo',
        km: this.routeData.km
      }
    }
  },
  created () {
    this.loadRoute()
    this.loadClients()
    this.loadProducts()
  },
  methods: {
    ...mapActions([
      'getRouteDetail',
      'getClients',
      'getProducts',
      'updateStatus',
      'updateDeliveredProducts',
      'updateDeliveryComments',
      'updateDeliveryNote'
    ]),
    loadRoute () {
      this.getRouteDetail(this.routeId)
        .then((res) => {
          if (res.exists) {
            this.routeData = res.data()
            this.routeData.date = this.dateWithDay(this.routeData.date)
            // Ordenar clientes de la ruta por posicion
            this.routeData.orderedClients = Object.keys(this.routeData.clients)
              .map(c => [c, this.routeData.clients[c].position])
              .sort((a, b) => a[1] - b[1])
          } else {
            // TODO
            console.warn('[getRouteDetail] No existe')
          }
          this.hasFinished.route = true
        })
        .catch((err) => {
          console.error('[getRouteDetail]')
          console.error(err)
        })
    },
    loadClients () {
      this.getClients()
        .then((res) => {
          res.forEach(doc => {
            this.clients.push(
              {
                id: doc.id,
                ...doc.data(),
                createdAt: this.dateDDMMYYYY(doc.data().createdAt)
              }
            )
          })
          this.hasFinished.clients = true
        })
        .catch((err) => {
          console.log('[getClients]')
          console.log(err)
        })
    },
    loadProducts () {
      this.getProducts()
        .then((res) => {
          res.forEach(doc => {
            this.products[doc.id] = { id: doc.id, ...doc.data(), createdAt: this.dateDDMMYYYY(doc.data().createdAt) }
          })
          this.hasFinished.products = true
        })
        .catch((err) => {
          console.log('[getProducts]')
          console.log(err)
        })
    },
    makeToast (title, msg, type) {
      this.$bvToast.toast(msg, {
        title: title,
        autoHideDelay: 5000,
        appendToast: false,
        solid: true,
        variant: type || 'success'
      })
    },
    onUpdateStatus (status) {
      const temp = this.routeData.status
      if (temp !== status) {
        this.routeData.status = status
        this.updateStatus({ id: this.routeId, status })
          .then(() => {
            this.makeToast('👍 · Estado Ruta', 'Estado actualizado correctamente')
          })
          .catch((err) => {
            this.makeToast(`👎 · Estado Ruta`, 'No se han guardado datos', 'danger')
            this.routeData.status = temp
            console.log('[updateStatus]')
            console.log(err)
          })
      }
    },
    getClient (clientId) {
      return this.clients.find(c => c.id === clientId)
    },
    saveQuantity (data) {
      // Update value in parent
      this.routeData.clients[data.clientId].deliveredProducts[data.productId] = data.deliveredQuantity
      const cli = this.clients.find(c => c.id === data.clientId)
      const obj = {
        routeId: this.routeId,
        clientId: data.clientId,
        productId: data.productId,
        deliveredQuantity: data.deliveredQuantity
      }
      this.updateDeliveredProducts(obj)
        .then(() => {
          this.makeToast(`👍 · ${cli.name} actualizado`, `Cantidad entregada: ${data.deliveredQuantity}`)
        })
        .catch((err) => {
          this.makeToast(`👎 · Cantidad`, `No se han guardado datos de '${cli.name}'`, 'danger')
          console.log('err')
          console.log(err)
        })
    },
    saveDeliveryNote (data) {
      // Update value in parent
      this.routeData.clients[data.clientId].deliveryNote = data.deliveryNote
      this.routeData.clients[data.clientId].paidQuantity = data.paidQuantity
      this.routeData.clients[data.clientId].deliveryStatus = data.deliveryStatus
      this.routeData.clients[data.clientId].isDeliveryPaid = data.isDeliveryPaid

      const obj = { routeId: this.routeId, ...data }
      const cli = this.clients.find(c => c.id === data.clientId)
      this.updateDeliveryNote(obj)
        .then(() => {
          this.makeToast(`👍 · ${cli.name} actualizado`, `Albarán actualizado: ${data.deliveryNote}`)
        })
        .catch((err) => {
          console.log('[updateDeliveryNote]')
          console.log(err)
          this.makeToast(`👎 · Albarán`, `No se han guardado datos de '${cli.name}'`, 'danger')
        })
    },
    saveDeliveryComment (data) {
      // Update in parent
      this.routeData.clients[data.clientId].deliveryComment = data.deliveryComment
      const cli = this.clients.find(c => c.id === data.clientId)
      const obj = {
        routeId: this.routeId,
        clientId: data.clientId,
        deliveryComment: data.deliveryComment
      }
      this.updateDeliveryComments(obj)
        .then(() => {
          this.makeToast(`👍 · ${cli.name} actualizado`, `Comentario actualizado`)
        })
        .catch((err) => {
          console.log('[updateDeliveryComments]')
          console.log(err)
          this.makeToast(`👎 · Error`, `No se han guardado datos de '${cli.name}'`, 'danger')
        })
    }
  }
}
</script>
